import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import * as XLSX from 'xlsx';
import { LocationItem, LocationService } from '../core/services/location.service';
import {
  TransportSummaryReportRow,
  TransportSummaryReportService,
} from '../core/services/transport-summary-report.service';
import { getProgramId } from '../shared/utils/storage-utils';
import {
  buildReportDownloadSuffix,
  formatReportDateDisplay,
  formatReportDatePickerLabel,
  openNativeDatePicker,
  resolveReportDateRange,
  toReportDateInputValue,
} from '../shared/utils/report-date-range';

export type TransportReportMode = 'booked' | 'delivered';

/** Figma Booked view: counts by date + campus. */
export interface TransportBookedSummaryRow {
  mealDate: string;
  campus: string;
  toCampus: number;
  toHome: number;
  roundTrip: number;
}

@Component({
  selector: 'transport-summary-report-component',
  standalone: true,
  templateUrl: './transport-summary-report.component.html',
  styleUrls: ['./transport-summary-report.component.css'],
  imports: [CommonModule, FormsModule, RouterModule],
})
export class TransportSummaryReportComponent implements OnInit {
  isInitialLoading = true;
  isRefreshing = false;
  errorMessage: string | null = null;
  isFilterPanelOpen = false;

  /** Figma: Booked = campus summary counts; Delivered = per-user detail with Provided by */
  reportMode: TransportReportMode = 'booked';

  centers: LocationItem[] = [];
  filterCenterName = '';
  filterTransportType = '';
  filterProvidedBy = '';
  transportTypeOptions: string[] = [];
  providedByOptions: string[] = [];

  /** Figma: custom date range only — no Monthly / Year / Month defaults. */
  private readonly timeframe = 'custom' as const;

  draftTransportType = '';
  draftProvidedBy = '';
  draftCenterName = '';
  draftFromDate = '';
  draftToDate = '';

  /** Defaults to today on open; staff can change the range. */
  customFromDate = toReportDateInputValue(new Date());
  customToDate = toReportDateInputValue(new Date());

  /** Delivered (detail) rows after client filters. */
  rows: TransportSummaryReportRow[] = [];
  /** Booked (aggregated) rows after client filters. */
  bookedSummaryRows: TransportBookedSummaryRow[] = [];
  private allRows: TransportSummaryReportRow[] = [];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly locationService: LocationService,
    private readonly reportService: TransportSummaryReportService,
  ) {}

  ngOnInit(): void {
    const token = sessionStorage.getItem('token');
    if (!token) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    const mode = (this.route.snapshot.data['transportMode'] as TransportReportMode) || 'booked';
    this.reportMode = mode === 'delivered' ? 'delivered' : 'booked';

    // Ensure today is set even if the fields were cleared somehow.
    if (!this.customFromDate || !this.customToDate) {
      const today = toReportDateInputValue(new Date());
      this.customFromDate = this.customFromDate || today;
      this.customToDate = this.customToDate || today;
    }

    this.locationService.getActiveLocations().subscribe({
      next: (response) => {
        this.centers = response.locations ?? [];
        this.loadReport();
      },
      error: () => {
        this.centers = [];
        this.loadReport();
      },
    });
  }

  get showProvidedBy(): boolean {
    return this.reportMode === 'delivered';
  }

  get hasDownloadableRows(): boolean {
    return this.reportMode === 'booked'
      ? this.bookedSummaryRows.length > 0
      : this.rows.length > 0;
  }

  goBack(): void {
    void this.router.navigate(['/manage-services/senior-center/reports']);
  }

  setReportMode(mode: TransportReportMode): void {
    if (this.reportMode === mode) {
      return;
    }
    this.reportMode = mode;
    if (mode === 'booked') {
      this.filterProvidedBy = '';
      this.draftProvidedBy = '';
    }
    this.applyClientFilters();
  }

  /** Display label: normalize "To Campus" / "To Center" → "To Center". */
  formatTransportType(value: string | null | undefined): string {
    const type = (value || '').trim();
    if (!type) {
      return '—';
    }
    const lower = type.toLowerCase();
    if (lower === 'to center' || lower === 'to campus') {
      return 'To Center';
    }
    if (lower === 'to home') {
      return 'To Home';
    }
    if (lower === 'round trip') {
      return 'Round Trip';
    }
    return type;
  }

  openFilterPanel(): void {
    this.syncDraftFromActive();
    this.isFilterPanelOpen = true;
  }

  closeFilterPanel(): void {
    this.isFilterPanelOpen = false;
  }

  resetFilterPanel(): void {
    this.draftTransportType = '';
    this.draftProvidedBy = '';
    this.draftCenterName = '';
    this.draftFromDate = '';
    this.draftToDate = '';
  }

  applyFilterPanel(): void {
    this.filterTransportType = this.draftTransportType;
    this.filterProvidedBy = this.draftProvidedBy;
    this.filterCenterName = this.draftCenterName;

    if (this.draftFromDate && this.draftToDate) {
      this.customFromDate = this.draftFromDate;
      this.customToDate = this.draftToDate;
    }

    this.isFilterPanelOpen = false;
    this.loadReport({ silent: true });
  }

  onFiltersChanged(): void {
    this.loadReport({ silent: true });
  }

  /** Figma table dates: "18-Jul-2020". */
  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const raw = value.slice(0, 10);
    const date = new Date(`${raw}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return formatReportDateDisplay(value);
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  openDatePicker(event: Event): void {
    openNativeDatePicker(event);
  }

  formatDatePickerLabel(value: string | null | undefined): string {
    return formatReportDatePickerLabel(value);
  }

  onDownload(): void {
    if (!this.hasDownloadableRows) {
      return;
    }

    const sheetRows =
      this.reportMode === 'booked'
        ? this.bookedSummaryRows.map((row) => ({
            Date: this.formatDate(row.mealDate),
            Center: row.campus,
            'To Center': row.toCampus,
            'To Home': row.toHome,
            'Round Trip': row.roundTrip,
          }))
        : this.rows.map((row) => ({
            'User Name': row.userName,
            'Transportation type': this.formatTransportType(row.transportType),
            'Provided by': row.providedBy || '—',
            Center: row.center,
            Date: this.formatDate(row.mealDate),
          }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    const sheetName = this.reportMode === 'booked' ? 'Transportation Booked' : 'Transportation Delivered';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(
      workbook,
      `transport-report-${this.reportMode}-${this.downloadSuffix()}.xlsx`,
    );
  }

  trackByRow(index: number, row: TransportSummaryReportRow): string {
    return `${row.userName}|${row.transportType}|${row.center}|${row.mealDate ?? ''}|${index}`;
  }

  trackByBookedSummary(index: number, row: TransportBookedSummaryRow): string {
    return `${row.mealDate}|${row.campus}|${index}`;
  }

  private syncDraftFromActive(): void {
    this.draftTransportType = this.filterTransportType;
    this.draftProvidedBy = this.filterProvidedBy;
    this.draftCenterName = this.filterCenterName;
    this.draftFromDate = this.customFromDate;
    this.draftToDate = this.customToDate;
  }

  private downloadSuffix(): string {
    return buildReportDownloadSuffix({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
  }

  private applyClientFilters(): void {
    let filtered = [...this.allRows];

    // Booked = opted transport not yet staff-delivered; Delivered = staff confirmed in Service Delivery.
    if (this.reportMode === 'delivered') {
      filtered = filtered.filter(
        (row) =>
          row.transportDelivered === true ||
          (row.transportDeliveryStatus || '').trim().toLowerCase() === 'delivered',
      );
    } else {
      filtered = filtered.filter(
        (row) =>
          row.transportDelivered !== true &&
          (row.transportDeliveryStatus || '').trim().toLowerCase() !== 'delivered',
      );
    }

    if (this.filterTransportType) {
      const want = this.filterTransportType.trim().toLowerCase();
      filtered = filtered.filter((row) => {
        const type = (row.transportType || '').trim().toLowerCase();
        if (want === 'to center' || want === 'to campus') {
          return type === 'to campus' || type === 'to center';
        }
        return type === want;
      });
    }
    if (this.filterProvidedBy) {
      const want = this.filterProvidedBy.trim().toLowerCase();
      filtered = filtered.filter((row) => (row.providedBy || '').trim().toLowerCase() === want);
    }

    if (this.reportMode === 'booked') {
      this.bookedSummaryRows = this.buildBookedSummary(filtered);
      this.rows = [];
    } else {
      this.rows = filtered;
      this.bookedSummaryRows = [];
    }
  }

  private buildBookedSummary(rows: TransportSummaryReportRow[]): TransportBookedSummaryRow[] {
    const byKey = new Map<string, TransportBookedSummaryRow>();

    for (const row of rows) {
      const mealDate = (row.mealDate || '').trim();
      const campus = (row.center || '').trim() || '—';
      const key = `${mealDate}|${campus}`;
      let summary = byKey.get(key);
      if (!summary) {
        summary = {
          mealDate,
          campus,
          toCampus: 0,
          toHome: 0,
          roundTrip: 0,
        };
        byKey.set(key, summary);
      }

      const type = (row.transportType || '').trim().toLowerCase();
      if (type === 'to center' || type === 'to campus') {
        summary.toCampus += 1;
      } else if (type === 'to home') {
        summary.toHome += 1;
      } else if (type === 'round trip') {
        summary.roundTrip += 1;
      }
    }

    return Array.from(byKey.values()).sort((a, b) => {
      const dateCmp = (a.mealDate || '').localeCompare(b.mealDate || '');
      if (dateCmp !== 0) {
        return dateCmp;
      }
      return a.campus.localeCompare(b.campus, undefined, { sensitivity: 'base' });
    });
  }

  private refreshFilterOptions(): void {
    const types = new Set<string>();
    const providers = new Set<string>();
    for (const row of this.allRows) {
      const type = this.formatTransportType(row.transportType);
      if (type && type !== '—') {
        types.add(type);
      }
      const providedBy = (row.providedBy || '').trim();
      if (providedBy && providedBy !== '—') {
        providers.add(providedBy);
      }
    }
    const sortFn = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    this.transportTypeOptions = Array.from(types).sort(sortFn);
    this.providedByOptions = Array.from(providers).sort(sortFn);

    if (this.filterTransportType && !types.has(this.filterTransportType)) {
      this.filterTransportType = '';
    }
    if (this.filterProvidedBy && !providers.has(this.filterProvidedBy)) {
      this.filterProvidedBy = '';
    }
  }

  private loadReport(options: { silent?: boolean } = {}): void {
    const range = resolveReportDateRange({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
    if (!range) {
      this.allRows = [];
      this.rows = [];
      this.bookedSummaryRows = [];
      this.errorMessage = null;
      this.isInitialLoading = false;
      this.isRefreshing = false;
      return;
    }

    if (!options.silent) {
      this.isInitialLoading = true;
    } else {
      this.isRefreshing = true;
    }
    this.errorMessage = null;

    this.reportService
      .getReport({
        fromDate: range.fromDate,
        toDate: range.toDate,
        center: this.filterCenterName || null,
        programId: getProgramId() ?? undefined,
      })
      .subscribe({
        next: (response) => {
          this.allRows = response.rows ?? [];
          this.refreshFilterOptions();
          this.applyClientFilters();
          this.isInitialLoading = false;
          this.isRefreshing = false;
        },
        error: () => {
          this.allRows = [];
          this.rows = [];
          this.bookedSummaryRows = [];
          this.errorMessage = 'Unable to load transport report.';
          this.isInitialLoading = false;
          this.isRefreshing = false;
        },
      });
  }
}
